import { TablePageLayout } from "../../../../../components/TablePage/TablePageLayout";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useSWR from "swr";
import type { WithPagination } from "../../../../../types/types";
import { endpoints } from "../../../../../endpoints";
import {
  defaultHandleSort,
  DEFAULT_PER_PAGE,
  formatDate,
  rowHover,
  useStoreState,
} from "../../../../../util/util";
import { ProductFormSchema } from "../../../../../types/types.PIM";
import type { Getter, Row } from "@tanstack/react-table";
import { StatusLeft } from "../../../../../components/Status/Status";
import { useRoutePath } from "../../../../../util/Routing";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { createChangePerPageHandler } from "../../../../../components/Pagination/util";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
  LinkButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import axios from "axios";
import type { AxiosError } from "axios";
import { useNotifications } from "../../../../../components/Notifications/NotificationsContext";
import { useAuthContext } from "../../../../../components/Auth";
import { LinkIcon } from "../../../../../components/Icons/Icons";
import { IDMobile } from "../../../../../components/IDs/IDs";
import { FilterBy } from "../../../../../components/FilterBy/FilterBy";
import type { ChipType } from "../../../../../components/Chips/Chips";
import { useDebounce } from "../../../../../util/hooks";
import {
  loadFilters,
  saveFilters,
  clearFilters,
} from "../../../../../util/filterStateManager";
import { applyFiltersToURL } from "../ProductsList.util";
import { TableCellTwoLine } from "../../../../../components/Table/TableCellTwoLine";
import { SortingRule } from "../../../../../components/Table/utils";

type FilterOption = { id: string; name: string };

type RequestTrackingFilters = {
  assignees: FilterOption[];
  // One entry per form, keyed by the live form's id and showing its current
  // name (requests submitted before a rename are included under it).
  form_options: FilterOption[];
  requested_by: FilterOption[];
  statuses: string[];
  templates: FilterOption[];
};

// Request Tracking only understands these filter keys (matching the
// `filter=` props passed to `FilterBy` below). `loadFilters` parses whatever
// happens to be in the URL, which can include filter keys left over from
// other pages (e.g. the main products list also uses "status" and
// "template", but with different values/semantics) — those must be dropped
// rather than forwarded to the product-creation-form endpoints, or the
// backend 400s on values/keys it doesn't recognize.
const KNOWN_FILTER_KEYS = new Set([
  "status",
  "assignee_id",
  "form_id",
  "requested_by_id",
  "template_id",
]);

export const RequestTrackingPage = () => {
  const [query, setQuery] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });

  const [offset, setOffset] = useState(query.offset ?? 0);
  const [perPage, setPerPage] = useState(query.perPage ?? DEFAULT_PER_PAGE);
  const [searchQuery, setSearchQuery] = useState(query.q ?? "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [tableData, setTableData] = useState<ProductFormSchema[]>([]);
  const [filtersFromUrl, setFiltersFromUrl] = useState<{
    [key: string]: string[];
  }>({});
  const [totalPaginationCount, setTotalPaginationCount] = useState<number>(0);
  const [pagination, setPagination] = useState({
    perPage,
    pageCount: 0,
    pageIndex: 0,
  });
  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const theme = useTheme();
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotifications();
  const { storefront_id } = useStoreState();
  const { adminPath } = useRoutePath();
  const { user } = useAuthContext();
  const history = useHistory();
  const location = useLocation();

  const filtersKey = useMemo(() => {
    if (!storefront_id) return null;

    const baseURL =
      endpoints.v2_storefronts_id_pim_product_creation_form(storefront_id);
    const activeFilters = Object.fromEntries(
      Object.entries(filtersFromUrl).filter(([key]) =>
        KNOWN_FILTER_KEYS.has(key)
      )
    );

    if (Object.keys(activeFilters).length === 0) {
      return `${baseURL}/filters`;
    }

    const queryString = applyFiltersToURL({
      params: new URLSearchParams(),
      filtersFromUrl: activeFilters,
    });

    return `${baseURL}/filters?${queryString}`;
  }, [storefront_id, filtersFromUrl]);

  const { data: filterOptions } = useSWR<RequestTrackingFilters>(filtersKey, {
    revalidateOnFocus: false,
  });

  const formSubmissionsKey = useMemo(() => {
    if (!storefront_id) return null;

    const params = new URLSearchParams(`offset=${offset}&limit=${perPage}`);
    if (debouncedSearchQuery) params.append("q", debouncedSearchQuery);
    if (sortingRules.sortBy) {
      params.append("sort_by", sortingRules.sortBy);
      params.append("order_by", sortingRules.orderBy || "asc");
    }

    const activeFilters = Object.fromEntries(
      Object.entries(filtersFromUrl).filter(([key]) =>
        KNOWN_FILTER_KEYS.has(key)
      )
    );

    return (
      endpoints.v2_storefronts_id_pim_product_creation_form(storefront_id) +
      "?" +
      applyFiltersToURL({ params, filtersFromUrl: activeFilters })
    );
  }, [
    storefront_id,
    offset,
    perPage,
    debouncedSearchQuery,
    sortingRules.sortBy,
    sortingRules.orderBy,
    filtersFromUrl,
  ]);

  const {
    data: formSubmissions,
    mutate: mutateFormSubmissions,
    isValidating: isValidatingFormSubmissions,
    error: errorFormSubmissions,
  } = useSWR<WithPagination<{ data: ProductFormSchema[] }>>(
    formSubmissionsKey,
    {
      revalidateOnFocus: false,
    }
  );

  const toChips = (items: FilterOption[]): ChipType[] =>
    items.map(({ id, name }) => ({ id, name }));

  const statusLabel = (value: string): string => {
    if (value === "pending") return t("In Progress");
    if (value === "completed") return t("Completed");
    if (value === "discarded") return t("Discarded");
    return t("Submitted");
  };

  const statusChips: ChipType[] = (filterOptions?.statuses ?? []).map((s) => ({
    id: s,
    name: statusLabel(s),
  }));
  const assigneeChips = toChips(filterOptions?.assignees ?? []);
  const formChips = toChips(filterOptions?.form_options ?? []);
  const requestedByChips = toChips(filterOptions?.requested_by ?? []);
  const templateChips = toChips(filterOptions?.templates ?? []);

  const preselectedStatuses = statusChips.filter((c) =>
    (filtersFromUrl["status"] ?? []).includes(String(c.id))
  );
  const preselectedAssignees = assigneeChips.filter((c) =>
    (filtersFromUrl["assignee_id"] ?? []).includes(String(c.id))
  );
  const preselectedForms = formChips.filter((c) =>
    (filtersFromUrl["form_id"] ?? []).includes(String(c.id))
  );
  const preselectedRequestedBy = requestedByChips.filter((c) =>
    (filtersFromUrl["requested_by_id"] ?? []).includes(String(c.id))
  );
  const preselectedTemplates = templateChips.filter((c) =>
    (filtersFromUrl["template_id"] ?? []).includes(String(c.id))
  );

  const handleFilterChange = ({
    values,
    filter,
  }: {
    values: ChipType[];
    filter: string;
  }) => {
    const newFilters = Object.fromEntries(
      Object.entries(filtersFromUrl).filter(([key]) =>
        KNOWN_FILTER_KEYS.has(key)
      )
    );

    newFilters[filter] = values.map((v) => String(v.id));

    if (newFilters[filter].length === 0) {
      delete newFilters[filter];
    }

    saveFilters(newFilters, history, location.pathname, {
      q: searchQuery || undefined,
    });
    setOffset(0);
  };

  const handleClearAllFilters = () => {
    clearFilters(history, location.pathname, {
      q: searchQuery || undefined,
    });
    setOffset(0);
  };

  const hasActiveFilters = Object.entries(filtersFromUrl).some(
    ([key, values]) => KNOWN_FILTER_KEYS.has(key) && values.length > 0
  );

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  const columns = useMemo(() => {
    const handleDiscardForm = async (index: number) => {
      try {
        await axios.patch(
          `/v2/storefronts/${storefront_id}/pim/product-creation-form/${formSubmissions?.data[index].id}/status`,
          { status: "discard" }
        );
        mutateFormSubmissions();
        notifySuccess(t("Product form discarded successfully"));
      } catch (error) {
        const axiosError = error as AxiosError;
        notifyError(
          axiosError.response?.data.message ??
            t("There was an error discarding the product form"),
          { error: axiosError }
        );
      }
    };

    return [
      {
        header: t("Form ID"),
        accessorKey: "number",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <IDMobile>{getValue()}</IDMobile>
        ),
      },
      {
        header: t("Product Name"),
        accessorKey: "product_name",
      },
      {
        header: t("Template & Form"),
        accessorKey: "template_name",
        cell: ({ row }: { row: Row<ProductFormSchema> }) => (
          <TableCellTwoLine
            primaryText={row.original.template?.template_name}
            secondaryText={row.original.form?.name}
          />
        ),
      },
      {
        header: t("Requested by"),
        accessorKey: "requested_by",
        cell: ({ row }: { row: Row<ProductFormSchema> }) => (
          <TableCellTwoLine
            primaryText={`${row.original.created_by_user.firstname} ${row.original.created_by_user.lastname}`}
            secondaryText={
              row.original.created_at
                ? formatDate(row.original.created_at)
                : undefined
            }
          />
        ),
      },
      {
        header: t("Assignee"),
        accessorKey: "assignee",
        cell: ({ row }: { row: Row<ProductFormSchema> }) => (
          <TableCellTwoLine
            primaryText={`${row.original.user?.firstname ?? ""} ${
              row.original.user?.lastname ?? ""
            }`.trim()}
            secondaryText={row.original.user?.email_address}
          />
        ),
      },
      {
        header: t("Status"),
        accessorKey: "status",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <StatusLeft
            color={
              getValue() === "discarded"
                ? "red"
                : getValue() === "pending"
                ? "yellow"
                : getValue() === "completed"
                ? "blue"
                : "green"
            }
            text={
              getValue() === "discarded"
                ? t("Discarded")
                : getValue() === "pending"
                ? t("In Progress")
                : getValue() === "completed"
                ? t("Completed")
                : t("Submitted")
            }
            textStyle={{ fontSize: "13px" }}
          />
        ),
      },
      {
        header: "",
        accessorKey: "actions",
        cell: ({ row }: { row: Row<ProductFormSchema> }) => {
          const copyToClipboard = (id: string) => {
            navigator.clipboard.writeText(
              `${window.location.origin}${adminPath}/pim/products/new/product-via-form/${id}`
            );
            notifySuccess(t("Link copied to clipboard"));
          };
          return (
            <>
              <LinkButtonMediumV2
                onClick={(e) => {
                  e.preventDefault();
                  copyToClipboard(row.original.id);
                }}
              >
                <LinkIcon
                  width={18}
                  height={18}
                  fill={theme.primaryButtonTextColor}
                />
              </LinkButtonMediumV2>
              {row.original.status !== "discarded" &&
                row.original.status !== "completed" &&
                row.original.created_by_user?.email_address ===
                  user?.email_address && (
                  <ButtonWithConfirmDialog
                    Button={(props) => <DeleteButton {...props} />}
                    testid="discard-form"
                    datatip={t("Discard form")}
                    datafor="discard-form-tooltip"
                    handleConfirm={() => handleDiscardForm(row.index)}
                    confirmMessage={t(
                      "This form will be marked as discarded and cannot be used to create a product. This action cannot be undone. Are you sure you want to continue?"
                    )}
                    confirmBtnLabel={t("Discard")}
                    confirmBtnType="destructive"
                    useV2Buttons={true}
                  />
                )}
            </>
          );
        },
      },
    ];
  }, [
    t,
    storefront_id,
    mutateFormSubmissions,
    notifySuccess,
    notifyError,
    adminPath,
    formSubmissions,
    user,
    theme,
  ]);

  useEffect(() => {
    const filters = loadFilters(location);
    setFiltersFromUrl(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);

  useEffect(() => {
    if (formSubmissions) {
      setTableData(formSubmissions.data);
    }
    setPagination({
      perPage,
      pageCount: Math.ceil(
        (formSubmissions?.pagination.total ?? DEFAULT_PER_PAGE) / perPage
      ),
      pageIndex: (formSubmissions?.pagination.offset ?? 0) / perPage + 1,
    });
    setTotalPaginationCount((prev) =>
      (formSubmissions?.pagination?.total ?? 0) > prev
        ? formSubmissions?.pagination?.total ?? 0
        : prev
    );
  }, [formSubmissions, perPage]);

  useEffect(() => {
    setQuery({ offset, perPage });
  }, [offset, perPage, setQuery]);

  return (
    <TablePageLayout
      searchQuery={searchQuery}
      searchPlaceholder={t("Search...")}
      onSearchChange={(e) => {
        setOffset(0);
        setSearchQuery(e.target.value);
      }}
      onSearchClear={() => {
        setSearchQuery("");
        setQuery({ q: undefined });
        setOffset(0);
      }}
      filtersSlot={
        <>
          <FilterBy
            setter={handleFilterChange}
            options={templateChips}
            header={t("Template")}
            filter="template_id"
            label={t("Template")}
            preselectedFilters={preselectedTemplates}
            size="small"
          />
          <FilterBy
            setter={handleFilterChange}
            options={formChips}
            header={t("Form")}
            filter="form_id"
            label={t("Form")}
            preselectedFilters={preselectedForms}
            size="small"
          />
          <FilterBy
            setter={handleFilterChange}
            options={requestedByChips}
            header={t("Requested By")}
            filter="requested_by_id"
            label={t("Requested By")}
            preselectedFilters={preselectedRequestedBy}
            size="small"
          />
          <FilterBy
            setter={handleFilterChange}
            options={assigneeChips}
            header={t("Assignee")}
            filter="assignee_id"
            label={t("Assignee")}
            preselectedFilters={preselectedAssignees}
            size="small"
          />
          <FilterBy
            setter={handleFilterChange}
            options={statusChips}
            header={t("Status")}
            filter="status"
            label={t("Status")}
            preselectedFilters={preselectedStatuses}
            size="small"
          />
        </>
      }
      showClearAll={hasActiveFilters}
      onClearAll={handleClearAllFilters}
      clearAllLabel={t("Clear All")}
      totalCount={totalPaginationCount}
      searchFilterCount={formSubmissions?.pagination.total ?? 0}
      totalCountLabel={t("requests")}
      perPageItems={[10, 20, 50]}
      perPage={perPage}
      onPerPageChange={changePerPage}
      columns={columns}
      data={tableData.map((form) => ({
        ...form,
        rowURL: `${adminPath}/pim/products/new/product-via-form/${form.id}`,
      }))}
      isLoading={isValidatingFormSubmissions}
      error={errorFormSubmissions}
      lastChildleftAlign={true}
      rowHover={rowHover}
      handleSort={handleSort}
      pagination={pagination}
      offset={offset ?? 0}
      onPageChange={(offset) => setOffset(offset)}
    />
  );
};
